import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import { getCategoryName } from '../utils/localizedContent';
import './Products.css';

const PRODUCTS_PER_PAGE = 24;
const FAVORITE_ICON_SIZE = 10;

function Products() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t, language } = useLanguage();
  const lang = language === 'de' ? 'de' : language === 'en' ? 'en' : 'lt';

  const getProductName = useCallback(
    (product) => product[`name_${lang}`] || product.name_lt || product.name_en || product.name_de || product.name || '',
    [lang]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const category = searchParams.get('category') || '';
  const tag = searchParams.get('tag') || '';
  const collection = searchParams.get('collection') || '';
  const sort = searchParams.get('sort') || 'created_at';
  const order = searchParams.get('order') || 'DESC';
  const [cardImageIndex, setCardImageIndex] = useState({});
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const loadMoreRef = useRef(null);
  const [cardDrag, setCardDrag] = useState(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const cardDragRef = useRef(null);
  const SWIPE_THRESHOLD_PX = 50;

  useEffect(() => {
    cardDragRef.current = cardDrag;
  }, [cardDrag]);

  const addToCartMutation = useMutation({
    mutationFn: async ({ product_id }) => {
      const sessionId = localStorage.getItem('sessionId') || 'sess_' + Date.now();
      localStorage.setItem('sessionId', sessionId);
      return apiClient.post(
        '/cart/add',
        { product_id, variant_id: null, quantity: 1 },
        { headers: { 'x-session-id': sessionId } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cart']);
      notification.success(t('addedToCart'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorAddingToCart'));
    },
  });

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { data: favorites = [] } = useQuery({
    queryKey: ['user-favorites'],
    queryFn: async () => {
      const res = await apiClient.get('/user/favorites');
      return res.data;
    },
    enabled: !!token,
  });
  const addFavoriteMutation = useMutation({
    mutationFn: async (productId) => apiClient.post('/user/favorites', { product_id: productId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['user-favorites']);
      queryClient.invalidateQueries(['favorites-count']);
      notification.success(t('addedToFavorites'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorAddingToCart'));
    },
  });
  const removeFavoriteMutation = useMutation({
    mutationFn: async (productId) => apiClient.delete(`/user/favorites/${productId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['user-favorites']);
      queryClient.invalidateQueries(['favorites-count']);
      notification.success(t('removedFromFavorites'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorRemovingFavorite'));
    },
  });
  const isFavorite = useCallback((productId) => favorites.some((f) => f.product_id === productId), [favorites]);
  const handleFavoriteClick = useCallback((e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      notification.info(t('loginRequiredFavorites'));
      return;
    }
    if (isFavorite(productId)) {
      removeFavoriteMutation.mutate(productId);
    } else {
      addFavoriteMutation.mutate(productId);
    }
  }, [token, isFavorite, addFavoriteMutation, removeFavoriteMutation, notification, t]);

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await apiClient.get('/categories');
      return res.data;
    },
  });
  const categories = categoriesData || [];

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['products', category, search, tag, collection, sort, order],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (tag) params.append('tag', tag);
      if (collection) params.append('collection', collection);
      params.append('sort', sort);
      params.append('order', order);
      params.append('page', String(pageParam));
      params.append('limit', String(PRODUCTS_PER_PAGE));
      const res = await apiClient.get(`/products?${params.toString()}`);
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage?.pagination;
      if (!p || p.page >= p.pages) return undefined;
      return p.page + 1;
    },
  });

  const products = useMemo(
    () => data?.pages?.flatMap((p) => p.products) ?? [],
    [data?.pages]
  );
  const hasMore = hasNextPage && !isFetchingNextPage;

  useEffect(() => {
    if (!hasMore || !loadMoreRef.current) return;
    const el = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchNextPage]);

  const handleSortChange = (e) => {
    const value = e.target.value;
    const [newSort, newOrder] = value ? value.split(':') : ['created_at', 'DESC'];
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSort);
    params.set('order', newOrder);
    params.delete('page');
    setSearchParams(params);
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    const params = new URLSearchParams(searchParams);
    if (value) params.set('category', value);
    else params.delete('category');
    params.delete('page');
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    params.set('sort', sort);
    params.set('order', order);
    setSearchParams(params);
  };

  const colorMap = {
    black: '#000000',
    juoda: '#000000',
    white: '#ffffff',
    balta: '#ffffff',
    grey: '#9ca3af',
    gray: '#9ca3af',
    pilka: '#9ca3af',
    navy: '#1e3a5f',
    red: '#b91c1c',
    raudona: '#b91c1c',
    blue: '#2563eb',
    mėlyna: '#2563eb',
  };

  const getColorSwatches = (product) => {
    if (!product.variants?.length) return [];
    const list = product.variants
      .map((v) => v.option_value || v.label || (v.options && v.options[0]?.value) || v.sku)
      .filter(Boolean);
    const uniq = [...new Set(list)];
    return uniq.slice(0, 6).map((label) => {
      const key = String(label).toLowerCase().replace(/\s+/g, '');
      const hex = colorMap[key] || '#cccccc';
      return { label, hex };
    });
  };

  const getCategoryLabel = (product) => {
    const name =
      language === 'en'
        ? (product.category_name_en || product.category_name)
        : language === 'de'
          ? (product.category_name_de || product.category_name)
          : (product.category_name || product.category_name_en || product.category_name_de);
    if (name) return name.toUpperCase();
    return null;
  };

  const getProductImages = (product) => {
    if (product.images && product.images.length > 0) return product.images;
    if (product.primary_image) return [product.primary_image];
    return [];
  };

  const setImageIndex = useCallback((productId, index) => {
    setCardImageIndex((prev) => ({ ...prev, [productId]: index }));
  }, []);

  const startCardDrag = useCallback((productId, startIndex, clientX, clientY) => {
    setCardDrag({ productId, startIndex, dragOffsetPx: 0 });
    dragStartRef.current = { x: clientX, y: clientY };
  }, []);

  const finishCardDrag = useCallback(() => {
    const d = cardDragRef.current;
    if (!d) return;
    const { productId, startIndex, dragOffsetPx } = d;
    const product = products.find((p) => p.id === productId);
    const images = product ? getProductImages(product) : [];
    const len = images.length;
    setCardDrag(null);
    cardDragRef.current = null;
    if (len <= 1) return;
    if (dragOffsetPx > SWIPE_THRESHOLD_PX) {
      setCardImageIndex((prev) => ({ ...prev, [productId]: (startIndex - 1 + len) % len }));
    } else if (dragOffsetPx < -SWIPE_THRESHOLD_PX) {
      setCardImageIndex((prev) => ({ ...prev, [productId]: (startIndex + 1) % len }));
    }
  }, [products]);

  // Attach global mouse listeners only when drag starts; avoid re-running on every dragOffsetPx change
  useEffect(() => {
    if (!cardDrag) return;
    const handleMove = (e) => {
      const clientX = e.clientX;
      const deltaX = clientX - dragStartRef.current.x;
      setCardDrag((prev) => (prev ? { ...prev, dragOffsetPx: deltaX } : null));
    };
    const handleUp = () => {
      finishCardDrag();
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend only on productId so listeners aren't re-attached on every drag move
  }, [cardDrag?.productId, finishCardDrag]);

  const handleCardTouchStart = useCallback((e, productId, currentIndex) => {
    if (e.touches.length === 0) return;
    const t = e.touches[0];
    startCardDrag(productId, currentIndex, t.clientX, t.clientY);
  }, [startCardDrag]);

  const handleCardTouchMove = useCallback((e) => {
    if (!cardDrag || !e.touches.length) return;
    const t = e.touches[0];
    const deltaX = t.clientX - dragStartRef.current.x;
    const deltaY = t.clientY - dragStartRef.current.y;
    setCardDrag((prev) => (prev ? { ...prev, dragOffsetPx: deltaX } : null));
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 8) e.preventDefault();
  }, [cardDrag]);

  const handleCardTouchEnd = useCallback(() => {
    finishCardDrag();
  }, [finishCardDrag]);

  useEffect(() => {
    if (!hoveredProductId || !products.length) return;
    const product = products.find((p) => p.id === hoveredProductId);
    const images = product ? (product.images?.length ? product.images : (product.primary_image ? [product.primary_image] : [])) : [];
    if (images.length <= 1) return;
    const id = setInterval(() => {
      setCardImageIndex((prev) => {
        const current = prev[hoveredProductId] ?? 0;
        const next = (current + 1) % images.length;
        return { ...prev, [hoveredProductId]: next };
      });
    }, 2500);
    return () => clearInterval(id);
  }, [hoveredProductId, products]);

  return (
    <div className="products-page container">
      <div className="products-page-header">
        <h1 className="products-page-title">{t('products')}</h1>
        <form onSubmit={handleSearch} className="products-search-form">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('search') + '...'}
          className="products-search-input"
          aria-label={t('searchProducts')}
        />
        <button type="submit" className="products-search-btn">{t('search')}</button>
        </form>
      </div>

      <div className="products-filters-row">
        <div className="products-filters-left">
          <div className="products-filters-group">
            <label htmlFor="products-category" className="products-filters-label">{t('category')}</label>
            <select
              id="products-category"
              className="products-filters-select"
              value={category}
              onChange={handleCategoryChange}
              aria-label={t('filterByCategory')}
            >
              <option value="">{t('allCategories')}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug}>{getCategoryName(c, language) || c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="products-filters-right">
          <div className="products-filters-group">
            <label htmlFor="products-sort" className="products-filters-label">{t('sortBy')}</label>
            <select
              id="products-sort"
              className="products-filters-select"
              value={`${sort}:${order}`}
              onChange={handleSortChange}
              aria-label={t('sortProducts')}
            >
              <option value="created_at:DESC">{t('newest')}</option>
              <option value="name:ASC">{t('nameAz')}</option>
              <option value="name:DESC">{t('nameZa')}</option>
              <option value="price:ASC">{t('priceAsc')}</option>
              <option value="price:DESC">{t('priceDesc')}</option>
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="products-loading">{t('loading')}</div>
      ) : (
        <>
          <div className="products-grid">
            {products.map((product) => {
              const swatches = getColorSwatches(product);
              const categoryLabel = getCategoryLabel(product);
              const images = getProductImages(product);
              const currentImageIndex = cardImageIndex[product.id] ?? 0;
              const currentImage = images[currentImageIndex];
              const hasMultipleImages = images.length > 1;
              return (
                <Link
                  key={product.id}
                  to={`/products/${product.slug}`}
                  className="product-card"
                >
                  <div
                    className="product-card-image-wrap"
                    onMouseEnter={() => setHoveredProductId(product.id)}
                    onMouseLeave={() => {
                      setHoveredProductId(null);
                      if (cardDrag?.productId === product.id) finishCardDrag();
                    }}
                    onMouseDown={
                      hasMultipleImages
                        ? (e) => {
                            e.preventDefault();
                            startCardDrag(product.id, currentImageIndex, e.clientX, e.clientY);
                          }
                        : undefined
                    }
                    onTouchStart={
                      hasMultipleImages
                        ? (e) => handleCardTouchStart(e, product.id, currentImageIndex)
                        : undefined
                    }
                    onTouchMove={hasMultipleImages ? handleCardTouchMove : undefined}
                    onTouchEnd={hasMultipleImages ? handleCardTouchEnd : undefined}
                    onTouchCancel={hasMultipleImages ? handleCardTouchEnd : undefined}
                    onWheel={
                      hasMultipleImages
                        ? (e) => {
                            const WHEEL_THRESHOLD = 30;
                            if (
                              Math.abs(e.deltaX) > Math.abs(e.deltaY) &&
                              Math.abs(e.deltaX) > WHEEL_THRESHOLD
                            ) {
                              e.preventDefault();
                              if (e.deltaX > 0) {
                                setImageIndex(
                                  product.id,
                                  (currentImageIndex + 1) % images.length
                                );
                              } else {
                                setImageIndex(
                                  product.id,
                                  (currentImageIndex - 1 + images.length) % images.length
                                );
                              }
                            }
                          }
                        : undefined
                    }
                  >
                    {hasMultipleImages ? (
                      <div
                        className="product-card-image-slider"
                        style={{
                          width: `${images.length * 100}%`,
                          transform: (() => {
                            const isDragging = cardDrag?.productId === product.id;
                            const idx = isDragging ? cardDrag.startIndex : currentImageIndex;
                            const offsetPx = isDragging ? cardDrag.dragOffsetPx : 0;
                            return `translateX(calc(-${(idx / images.length) * 100}% + ${offsetPx}px))`;
                          })(),
                          transition: cardDrag?.productId === product.id ? 'none' : 'transform 0.35s ease-out',
                        }}
                      >
                        {images.map((src, i) => (
                          <div
                            key={i}
                            className="product-card-image-slide"
                            style={{ flex: `0 0 ${100 / images.length}%` }}
                          >
                            <img
                              src={src}
                              alt={`${getProductName(product)} ${i + 1}`}
                              loading={i === 0 ? 'lazy' : 'lazy'}
                            />
                          </div>
                        ))}
                      </div>
                    ) : currentImage ? (
                      <img src={currentImage} alt={getProductName(product)} loading="lazy" />
                    ) : (
                      <div className="product-card-placeholder" aria-hidden="true" />
                    )}
                    {hasMultipleImages && (
                      <div
                        className="product-card-image-dots"
                        role="tablist"
                        aria-label={`Nuotraukos: ${images.length}`}
                        onClick={(e) => e.preventDefault()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {images.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={i === currentImageIndex}
                            aria-label={`Nuotrauka ${i + 1}`}
                            className={`product-card-image-dot ${i === currentImageIndex ? 'active' : ''}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setImageIndex(product.id, i);
                            }}
                          />
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="product-card-favorite-btn"
                      onClick={(e) => handleFavoriteClick(e, product.id)}
                      aria-label={isFavorite(product.id) ? t('removeFromFavorites') : t('addToFavorites')}
                      title={isFavorite(product.id) ? t('removeFromFavorites') : t('addToFavorites')}
                    >
                      {isFavorite(product.id) ? (
                        <AiFillHeart size={FAVORITE_ICON_SIZE} aria-hidden />
                      ) : (
                        <AiOutlineHeart size={FAVORITE_ICON_SIZE} aria-hidden />
                      )}
                    </button>
                    {product.salePrice && (
                      <span className="product-card-badge">{t('sale')}</span>
                    )}
                  </div>
                  <div className="product-card-info">
                    {swatches.length > 0 && (
                      <div className="product-card-swatches" aria-hidden="true">
                        {swatches.map((s, i) => (
                          <span
                            key={i}
                            className={`product-card-swatch ${i === 0 ? 'product-card-swatch-selected' : 'product-card-swatch-outline'}`}
                            style={i === 0 ? { backgroundColor: s.hex } : {}}
                            title={s.label}
                          />
                        ))}
                      </div>
                    )}
                    {categoryLabel && (
                      <p className="product-card-category">{categoryLabel}</p>
                    )}
                    <h3 className="product-card-title">{getProductName(product)}</h3>
                    <div className="product-card-price">
                      <span className="product-card-price-current">
                        {product.salePrice
                          ? parseFloat(product.salePrice).toFixed(2)
                          : product.price
                            ? parseFloat(product.price).toFixed(2)
                            : '0.00'}{' '}
                        €
                      </span>
                      {(product.salePrice && (product.originalPrice || product.price)) ||
                      product.compare_price ? (
                        <span className="product-card-price-compare">
                          {product.salePrice
                            ? parseFloat(product.originalPrice || product.price).toFixed(2)
                            : parseFloat(product.compare_price).toFixed(2)}{' '}
                          €
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="product-card-add-to-cart"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addToCartMutation.mutate({ product_id: product.id });
                      }}
                      disabled={addToCartMutation.isLoading}
                    >
                      {t('addToCart')}
                    </button>
                  </div>
                </Link>
              );
            })}
          </div>

          <div ref={loadMoreRef} className="products-infinite-sentinel" aria-hidden="true" />
          {isFetchingNextPage && (
            <div className="products-loading-more">{t('loadMore')}</div>
          )}
        </>
      )}
    </div>
  );
}

export default Products;
